// ✅ Tasks (Phase C2) — the staff task list + PIN-attributed complete
// (index.html tab). ToyTalk "Do" replacement, doing surface.
//
// Product rules:
//   - The list shows TODAY'S occurrences for THIS store (the Worker computes
//     the Dubai business day — 3am rule), urgent first, completed greyed.
//   - COMPLETING is per store per occurrence; the completing PERSON proves
//     who they are with their PIN (memory only, 5-min idle — chat A3 posture).
//     Tasks with evidence='photo' REQUIRE a photo (canvas-compressed like
//     chat A6-1) before the PIN modal opens.
//   - The who-record is SHARED with chat/news ('chat_who_v1') — one pick.
//   - Accounts (AM / HO / ho_staff) in the staff app get a read-only view of
//     any store's list (their compliance surface is the admin app).
//
// Bare-name globals from index.html (currentScreen, currentStore, currentUser,
// getStoreStaff, DB) are typeof-guarded — the classic-script bundle contract.

import { openStoredFile } from '../shared/open-file.js';   // v129.112 — ONE opener (Head Office's PDF)
// v129.113 — SEVERAL reference files per task (three Head Office colleagues, 13 Aug).
import { attachLinksHtml, openFromPayload } from '../shared/attachments-ui.js';

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function initials(name) {
  var p = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  return (p[0][0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
}

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}

function isAccountMode() {
  try {
    return !!(typeof currentUser !== 'undefined' && currentUser &&
      ['admin', 'area_manager', 'ho_staff'].indexOf(String(currentUser.role)) !== -1 &&
      typeof DB !== 'undefined' && DB.getToken && DB.getToken());
  } catch (e) { return false; }
}

// ── State (the PIN is deliberately NOT persisted anywhere) ────────────────────
var _who = null;                 // { id, name } — SHARED key 'chat_who_v1'
var _pin = null;                 // in-memory only
var _pinAt = 0;
var PIN_IDLE_MS = 5 * 60 * 1000;
var _tasks = [];
var _date = null;
var _completing = false;
var _evidence = {};              // task id → compressed data-URL awaiting complete
var _evidenceName = {};          // v127.36 — task id → original filename for FILE evidence (null for photos)
// v129.109 (Arabian Centre: "I want to upload more than 1 pictures in task for
// 1 pager") — _evidence[id] / _evidenceName[id] are now ARRAYS. The task list is
// the only reader and it goes through evidenceList(), so nothing else has to
// know the shape changed.
var TK_MAX_EVIDENCE = 3;         // matches the Worker's MAX_EVIDENCE_FILES
function evidenceList(id) { return Array.isArray(_evidence[id]) ? _evidence[id] : (_evidence[id] ? [_evidence[id]] : []); }
function evidenceNames(id) { return Array.isArray(_evidenceName[id]) ? _evidenceName[id] : (_evidenceName[id] ? [_evidenceName[id]] : []); }
var _journal = null;             // E1: { target, brief, brief_by, todos } — the store's daily page
var _editingBrief = false;
var _surveyCount = -1;           // v125.3: open surveys for THIS person (-1 = not loaded)
var _missed = [];                // v125.16 (review D): recurring occurrences the store missed in the last 2 days
var _q = '';                     // v126.65: staff search across title + body
var _scope = 'all';              // v126.65: 'all' | 'me' (me = still to do — not completed)
var _history = [];               // v129.82 — completed past tasks (Meadows: "once clicked off cant see it")
var _showHistory = false;
var _histOpen = {};              // v129.100 — which past-task rows are expanded (Meadows: "read again the details")
var _embedded = false;           // v126.66: inside the Updates hub → hide our own bar (the hub shows one shared)

function loadWho() {
  if (_who) return _who;
  try {
    var raw = localStorage.getItem('chat_who_v1');
    if (raw) { var w = JSON.parse(raw); if (w && w.id && w.name) _who = { id: w.id, name: w.name }; }
  } catch (e) { /* fresh pick */ }
  return _who;
}
function saveWho(w) {
  _who = w ? { id: w.id, name: w.name } : null;  // id + name ONLY — never a PIN
  try {
    if (_who) localStorage.setItem('chat_who_v1', JSON.stringify(_who));
    else localStorage.removeItem('chat_who_v1');
  } catch (e) { /* storage unavailable — session-only */ }
}
function pinFresh() { return _pin && (Date.now() - _pinAt) < PIN_IDLE_MS; }
function clearPin() { _pin = null; _pinAt = 0; }

function root() { return document.getElementById('tasksRoot'); }
function onTasksScreen() {
  // v126.66: 'updates' is the hub that hosts the tasks root — treat it as on-screen
  // so background refreshes still repaint the list.
  return (typeof currentScreen === 'undefined' || currentScreen === 'tasks' || currentScreen === 'updates') && !document.hidden;
}

// ── Styles (own tk- namespace) ────────────────────────────────────────────────
var _styled = false;
function ensureStyles() {
  if (_styled) return;
  _styled = true;
  var css = '' +
    '#tasksRoot .tk-shell{position:relative;max-width:680px;margin:0 auto;background:var(--bg,#f0f2f5);border-radius:12px;overflow:hidden;display:flex;flex-direction:column;min-height:60vh}' +
    '#tasksRoot .tk-head{background:var(--brand,#009E3D);color:#fff;padding:12px 16px;display:flex;align-items:center;gap:10px}' +
    '#tasksRoot .tk-hname{font-weight:700;font-size:16px}' +
    '#tasksRoot .tk-hsub{font-size:11.5px;opacity:.85}' +
    '#tasksRoot .tk-feed{padding:12px;display:flex;flex-direction:column;gap:10px}' +
    '#tasksRoot .tk-card{background:var(--card);border-radius:10px;padding:12px 14px;box-shadow:0 1px 1px rgba(0,0,0,.08)}' +
    '#tasksRoot .tk-card.tk-done{opacity:.55}' +
    // v135.1: logical sides (border-inline-start, margin-inline-start) render the same in English and mirror for Arabic.
    '#tasksRoot .tk-card.tk-urgent{border-inline-start:4px solid #e53935}' +
    '#tasksRoot .tk-card.tk-high{border-inline-start:4px solid #fb8c00}' +
    '#tasksRoot .tk-title{font-weight:700;font-size:15px;color:var(--text);display:flex;align-items:center;gap:6px;flex-wrap:wrap}' +
    '#tasksRoot .tk-chip{font-size:10px;font-weight:800;border-radius:8px;padding:1px 7px;color:#fff}' +
    '#tasksRoot .tk-chip.u{background:#e53935}#tasksRoot .tk-chip.h{background:#fb8c00}' +
    '#tasksRoot .tk-body{font-size:13.5px;color:var(--comms-body);margin-top:6px;white-space:pre-wrap;word-break:break-word}' +
    '#tasksRoot .tk-meta{font-size:11px;color:var(--comms-mute);margin-top:8px}' +
    '#tasksRoot .tk-go{margin-top:10px;background:var(--brand,#009E3D);color:#fff;border:none;border-radius:8px;padding:8px 14px;font-weight:700;font-size:13px;cursor:pointer}' +
    '#tasksRoot .tk-donetxt{margin-top:10px;color:var(--comms-brand-ink);font-weight:700;font-size:13px}' +
    '#tasksRoot .tk-empty{padding:28px;text-align:center;color:var(--comms-sub);font-size:13.5px}' +
    '#tasksRoot .tk-notice{background:var(--comms-note-bg);color:var(--comms-note-ink);font-size:12px;padding:8px 12px;text-align:center}' +
    '#tasksRoot .tk-pick{background:var(--card);max-height:50vh;overflow-y:auto}' +
    '#tasksRoot .tk-item{display:flex;gap:10px;padding:10px 14px;border-bottom:1px solid var(--surface-3);cursor:pointer;align-items:center}' +
    '#tasksRoot .tk-iav{width:36px;height:36px;border-radius:50%;background:var(--brand,#009E3D);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;flex:0 0 36px}' +
    '#tasksRoot .tk-modal{position:fixed;inset:0;background:rgba(11,20,26,.6);display:flex;align-items:center;justify-content:center;z-index:3000}' +
    '#tasksRoot .tk-mcard{background:var(--card);border-radius:12px;padding:20px;width:min(340px,90vw);text-align:center}' +
    '#tasksRoot .tk-mpin{width:100%;font-size:22px;letter-spacing:8px;text-align:center;padding:10px;border:2px solid var(--text);border-radius:10px;margin:14px 0}' +
    '#tasksRoot .tk-mbtn{width:100%;background:var(--brand,#009E3D);color:#fff;border:none;border-radius:10px;padding:11px;font-weight:700;font-size:14px;cursor:pointer}' +
    '#tasksRoot .tk-mcancel{margin-top:10px;color:var(--comms-sub);background:none;border:none;font-size:13px;cursor:pointer}';
  var el = document.createElement('style');
  el.id = 'tasksStyles';
  el.textContent = css;
  document.head.appendChild(el);
}

// ── Screen entry point (called by switchScreen('tasks')) ─────────────────────
// v125.51 (Matt: "pin should only come after confirming the news. Same with
// tasks"): the list renders IMMEDIATELY, keyed by the signed-in store when
// nobody has picked a name yet. The who-picker appears only on demand
// (completing a task / writing the journal), and the PIN only after that.
function renderTasksScreen() {
  ensureStyles();
  loadWho();          // restore a previous pick if there is one
  renderList();
  refreshTasks();
  refreshJournal();
}

// One action waiting on a who-pick (e.g. tapped Complete before picking a name).
var _pendingAction = null;

// ── 📔 The store daily journal (E1 — the post-it killer) ─────────────────────
function journalStore() {
  try {
    if (typeof currentStore !== 'undefined' && currentStore) return currentStore.name || currentStore;
  } catch (e) { /* none */ }
  return '';
}

async function refreshJournal() {
  try {
    var q;
    if (isAccountMode()) {
      var store = journalStore();
      if (!store) return;
      q = '/api/journal?store=' + encodeURIComponent(store);
    } else if (_who) {
      q = '/api/journal?staff_id=' + encodeURIComponent(_who.id);
    } else {
      // v125.51: read-before-pick — the journal reads by store; writes still PIN.
      var jst = journalStore();
      if (!jst) return;
      q = '/api/journal?store=' + encodeURIComponent(jst);
    }
    var res = await api(q);
    if (!res.ok) return;
    var d = await res.json();
    if (d && d.ok) {
      _journal = d;
      if (onTasksScreen()) renderList();
    }
  } catch (e) { /* the journal is best-effort — tasks render without it */ }
  // v125.3: surveys live in this hub now (no nav tab) — count the open ones.
  try {
    if (_who && !isAccountMode()) {
      var sres = await api('/api/surveys?staff_id=' + encodeURIComponent(_who.id));
      if (sres.ok) {
        var sd = await sres.json();
        if (sd && sd.ok) {
          _surveyCount = (sd.surveys || []).filter(function (s) { return !s.responded; }).length;
          updateNavBadge();
          if (onTasksScreen()) renderList();
        }
      }
    }
  } catch (e) { /* the card just shows without a count */ }
}

// The 📋 Surveys entry card — surveys are deadline-work, so they live in the
// Tasks hub (docs/ui-audit-v125.md Finding 3). Tapping opens the surveys screen.
// v125.16 (review C + D): a compact attention strip at the TOP of the feed —
// above the journal so the day's target stays first — surfacing the two slips
// the store used to never see: surveys awaiting an answer, and recurring tasks
// missed in the last couple of days. Both are also driven by the background
// poll now, so a new survey lights the Tasks tab amber even from the Clock tab.
function attentionStripHtml() {
  if (isAccountMode()) return '';
  var out = '';
  if (_surveyCount > 0) {
    out += '<div onclick="if (typeof switchScreen === \'function\') switchScreen(\'surveys\')" ' +
      'style="cursor:pointer;background:var(--brand-soft,#E6F5EC);color:var(--comms-brand-ink);font-weight:700;font-size:13px;padding:9px 12px;border-radius:8px;margin-bottom:8px;display:flex;align-items:center;gap:8px">' +
      '📋 ' + _surveyCount + ' survey' + (_surveyCount === 1 ? '' : 's') + ' to answer by the deadline' +
      '<span style="margin-inline-start:auto;font-weight:400">Open →</span></div>';
  }
  if (_missed && _missed.length) {
    var names = _missed.slice(0, 3).map(function (m) { return esc(m.title); }).join(', ');
    var more = _missed.length > 3 ? ' +' + (_missed.length - 3) + ' more' : '';
    out += '<div style="background:var(--comms-peach-bg);color:var(--comms-peach-ink);font-weight:600;font-size:12px;padding:9px 12px;border-radius:8px;margin-bottom:8px">' +
      '⚠️ Missed in the last 2 days: <span translate="no">' + names + '</span>' + more +
      '<div style="font-weight:400;font-size:11px;margin-top:2px">Catch up if you can, and make sure today\'s are done.</div></div>';
  }
  return out;
}

function surveysCardHtml() {
  if (isAccountMode()) return '';
  var badge = _surveyCount > 0
    ? '<span class="tk-chip" style="background:var(--brand,#009E3D)">' + _surveyCount + ' TO ANSWER</span>'
    : (_surveyCount === 0 ? '<span style="color:var(--comms-brand-ink);font-weight:700;font-size:12px">✓ all answered</span>' : '');
  return '<div class="tk-card" style="cursor:pointer" onclick="if (typeof switchScreen === \'function\') switchScreen(\'surveys\')">' +
    '<div class="tk-title">📋 Surveys ' + badge +
    '<span style="margin-inline-start:auto;color:var(--comms-mute);font-weight:400;font-size:13px">Open →</span></div>' +
    '<div class="tk-meta" style="margin-top:4px">Quick questions from Head Office — answer by their deadline.</div></div>';
}

function journalHtml() {
  if (!_journal) return '';
  var acct = isAccountMode();
  var j = _journal;
  var target = j.target != null
    ? '<div style="font-size:14px;font-weight:800;color:var(--comms-brand-text)">🎯 Today\'s target: ' + esc(String(j.target)) + '</div>'
    : '<div style="font-size:12px;color:var(--comms-mute)">🎯 No target loaded for today</div>';
  var brief;
  if (_editingBrief && !acct) {
    var draft = briefDraft();                       // v125.16: a failed-save draft wins over the server copy
    var draftHint = (draft != null && draft !== (j.brief || ''))
      ? '<div style="font-size:11px;color:var(--comms-peach-ink);margin-top:4px">⚠️ Unsaved draft kept on this device — tap Save to retry.</div>' : '';
    brief = '<textarea id="tkBrief" maxlength="2000" style="width:100%;min-height:70px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px;margin-top:8px;box-sizing:border-box;font-family:inherit">' + esc(draft != null ? draft : (j.brief || '')) + '</textarea>' + draftHint +
      '<button class="tk-go" style="margin-top:6px" onclick="Tasks.saveBrief()">Save brief</button>' +
      '<button class="tk-mcancel" onclick="Tasks.editBrief(false)">Cancel</button>';
  } else {
    // v135.1: the brief is what the store wrote (translate="no"); the empty-state line is ours.
    brief = '<div style="font-size:13px;color:var(--comms-body);white-space:pre-wrap;margin-top:8px"' + (j.brief ? ' translate="no"' : '') + '>' +
      (j.brief ? esc(j.brief) : '<span style="color:var(--comms-mute)">No brief written yet today.</span>') + '</div>' +
      (j.brief && j.brief_by ? '<div class="tk-meta">📔 by <span translate="no">' + esc(j.brief_by) + '</span></div>' : '') +
      (!acct ? '<button class="tk-go" style="margin-top:6px;background:var(--brand,#009E3D)" onclick="Tasks.editBrief(true)">' + (j.brief ? '✏️ Update the brief' : '📔 Write today\'s brief') + '</button>' : '');
  }
  // v129.114 — "from Mon 11 Aug" for a carried to-do. Built from the date STRING,
  // never `new Date(str)` off a bare Y-M-D: Dubai is UTC+4, so parsing it as UTC
  // and formatting it locally is how a date drifts a day (the house rule).
  var shortDay = function (ymd) {
    var s = String(ymd || '');
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return s;
    var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return String(Number(m[3])) + ' ' + (MON[Number(m[2]) - 1] || m[2]);
  };
  var todos = (j.todos || []).map(function (t) {
    // v129.114 (Marina Mall) — a to-do carried over from an earlier day says so.
    // Ticking or removing it works exactly as before: the write goes by row id,
    // so it updates the ORIGINAL entry and it stops carrying forward.
    var carried = t.carried
      ? '<span style="font-size:10px;font-weight:800;background:var(--comms-peach-bg);color:var(--comms-peach-ink);border-radius:8px;padding:1px 6px;margin-inline-start:6px;white-space:nowrap">from ' + esc(shortDay(t.from_date)) + '</span>'
      : '';
    return '<div style="display:flex;align-items:center;gap:8px;margin-top:6px;font-size:13px">' +
      (acct
        ? '<span>' + (t.done ? '✅' : '⬜') + '</span>'
        : '<span style="cursor:pointer;font-size:16px" onclick="Tasks.toggleTodo(' + t.id + ',' + (t.done ? 'false' : 'true') + ')">' + (t.done ? '✅' : '⬜') + '</span>') +
      '<span style="flex:1;' + (t.done ? 'text-decoration:line-through;color:var(--comms-mute)' : 'color:var(--text)') + '"><span translate="no">' + esc(t.text) + '</span>' + carried +
      (t.done && t.done_by ? ' <span style="font-size:11px;color:var(--comms-mute)" translate="no">— ' + esc(t.done_by) + '</span>' : '') + '</span>' +
      (!acct ? '<span style="cursor:pointer;color:var(--comms-mute)" title="Remove" onclick="Tasks.deleteTodo(' + t.id + ')">✕</span>' : '') +
      '</div>';
  }).join('');
  // The tail beyond the carry cap is COUNTED, never silently dropped — a store
  // with 30 outstanding jobs needs to know that, not see a tidy list of ten.
  var carriedNote = (j.carried_hidden > 0)
    ? '<div style="font-size:11px;color:var(--comms-peach-ink);margin-top:6px">+ ' + j.carried_hidden + ' more still outstanding from earlier days.</div>'
    : '';
  var add = acct ? '' :
    '<div style="display:flex;gap:6px;margin-top:8px">' +
    '<input id="tkTodoIn" maxlength="300" placeholder="Add a store to-do…" style="flex:1;padding:7px 9px;border:1px solid var(--border-strong);border-radius:8px;font-size:12.5px">' +
    '<button class="tk-go" style="margin-top:0;padding:7px 12px" onclick="Tasks.addTodo()">Add</button></div>';
  return '<div class="tk-card" style="border-inline-start:4px solid var(--brand,#009E3D)">' + target + brief +
    (!acct && j.suggestions && j.suggestions.length
      ? '<div style="font-weight:700;font-size:13px;color:var(--text);margin-top:10px">💡 Suggested</div>' +
        j.suggestions.map(function (sg, i) {
          return '<div style="display:flex;align-items:center;gap:8px;margin-top:6px;font-size:12.5px;color:var(--comms-body)">' +
            '<span style="flex:1">' + esc(sg.text) + '</span>' +
            '<button class="tk-go" style="margin-top:0;padding:5px 10px;font-size:11.5px" onclick="Tasks.addSuggested(' + i + ')">＋ Add</button></div>';
        }).join('')
      : '') +
    '<div style="font-weight:700;font-size:13px;color:var(--text);margin-top:10px">☑ Store to-dos</div>' +
    (todos || '<div style="font-size:12px;color:var(--comms-mute);margin-top:4px">Nothing yet — add the day\'s jobs here, not on a post-it. 📝</div>') +
    carriedNote + add + '</div>';
}

// 💡 Copilot accept: the suggestion becomes a normal store to-do (PIN flow reused).
function addSuggested(i) {
  var sg = _journal && _journal.suggestions && _journal.suggestions[i];
  if (!sg) return;
  var go = function () { doJournalPost('/api/journal/todo', { text: sg.text }); };
  if (pinFresh()) go(); else askPin(go);
}

function editBrief(on) { _editingBrief = !!on; renderList(); }

// v125.16 (review E): the journal brief can be a paragraph of typing — if the
// save fails on a flaky store connection it used to be lost the moment the
// screen re-rendered. Stash it on this device (keyed store+day, brief text only
// — never a PIN) and restore it into the editor, like the logs draft pattern.
var BRIEF_STASH = 'journal_brief_stash_v1';
function briefStashCtx() {
  var store = isAccountMode() ? journalStore() : (_who ? _who.store : '');
  return { store: store || '', date: _date || '' };
}
function stashBrief(text) {
  try {
    var c = briefStashCtx();
    localStorage.setItem(BRIEF_STASH, JSON.stringify({ store: c.store, date: c.date, text: String(text || '') }));
  } catch (e) { /* storage full/blocked — nothing we can do, the toast already warned */ }
}
function briefDraft() {
  try {
    var raw = localStorage.getItem(BRIEF_STASH);
    if (!raw) return null;
    var s = JSON.parse(raw);
    var c = briefStashCtx();
    return (s && s.store === c.store && s.date === c.date && s.text) ? String(s.text) : null;
  } catch (e) { return null; }
}
function clearBriefStash() { try { localStorage.removeItem(BRIEF_STASH); } catch (e) {} }

// v125.51: identity on demand — if nobody has picked a name yet, open the
// picker and resume the write after. The closures capture their text/ids, so
// nothing typed is lost across the repaint.
function needWho(then) {
  if (_who) return false;
  _pendingAction = then;
  renderWhoPicker();
  return true;
}
function saveBrief() {
  var el = document.getElementById('tkBrief');
  var text = el ? String(el.value || '') : '';
  var go = function () { doJournalPost('/api/journal/brief', { brief: text }, function () { _editingBrief = false; clearBriefStash(); }); };
  if (needWho(function () { askPin(go); })) return;
  if (pinFresh()) go(); else askPin(go);
}
function addTodo() {
  var el = document.getElementById('tkTodoIn');
  var text = el ? String(el.value || '').trim() : '';
  if (!text) return;
  var go = function () { doJournalPost('/api/journal/todo', { text: text }); };
  if (needWho(function () { askPin(go); })) return;
  if (pinFresh()) go(); else askPin(go);
}
function toggleTodo(id, done) {
  var go = function () { doJournalPost('/api/journal/todo-done', { id: id, done: done }); };
  if (needWho(function () { askPin(go); })) return;
  if (pinFresh()) go(); else askPin(go);
}
function deleteTodo(id) {
  var go = function () { doJournalPost('/api/journal/todo-delete', { id: id }); };
  if (needWho(function () { askPin(go); })) return;
  if (pinFresh()) go(); else askPin(go);
}
async function doJournalPost(path, payload, after) {
  try {
    var res = await api(path, {
      method: 'POST',
      body: JSON.stringify(Object.assign({}, payload, { auth_staff_id: _who.id, auth_pin: _pin })),
    });
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) {
      _pinAt = Date.now();
      if (after) after();
      refreshJournal();
    } else {
      clearPin();
      if (res.status === 401 || res.status === 429) askPin(function () { doJournalPost(path, payload, after); }, (d && d.error) || 'PIN check failed — try again');
      else {
        if (path === '/api/journal/brief') stashBrief(payload && payload.brief);  // v125.16 (E): don't lose the typing
        if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not save', 'error');
      }
    }
  } catch (e) {
    if (path === '/api/journal/brief') stashBrief(payload && payload.brief);       // v125.16 (E): offline — keep it
    if (typeof showToast !== 'undefined') showToast('No connection — kept on this device, try Save again', 'error');
  }
}

function renderWhoPicker() {
  var el = root(); if (!el) return;
  var staff = [];
  // v127.42 (a store manager's report #2, task-close picker 29 Jul): the identity picker shows
  // home staff + TODAY'S ROSTERED COVER (getStoreRoster's v113.0 second arg),
  // so a supporter transferred to another store can act under their own name
  // at the store they're covering — the same rule as clock/checks/tally.
  // Falls back to home-only where the roster helper isn't loaded (chat.html).
  try {
    if (typeof currentStore !== 'undefined') {
      var _rosterDay = (typeof businessDayStr === 'function') ? businessDayStr() : null;
      if (typeof getStoreRoster === 'function') staff = getStoreRoster(currentStore, _rosterDay) || [];
      else if (typeof getStoreStaff !== 'undefined') staff = getStoreStaff(currentStore) || [];
    }
  } catch (e) { staff = []; }
  var rows = staff.map(function (s) {
    return '<div class="tk-item" onclick="Tasks.pickWho(' + s.id + ',\'' + esc(String(s.name || '').replace(/'/g, '')) + '\')">' +
      '<div class="tk-iav" translate="no">' + esc(initials(s.name)) + '</div>' +
      '<div><div style="font-weight:600;font-size:14px;color:var(--text)" translate="no">' + esc(s.name) + '</div>' +
      '<div style="font-size:12px;color:var(--comms-mute)">' + esc(s.role || '') + '</div></div></div>';
  }).join('');
  el.innerHTML = '<div class="tk-shell">' +
    '<div class="tk-head"><div style="font-size:22px">✅</div><div><div class="tk-hname">Tasks</div><div class="tk-hsub">Who are you?</div></div></div>' +
    '<div class="tk-notice">Pick your name so this action is recorded as you. You\'ll only need your PIN to confirm.</div>' +
    '<div class="tk-pick">' + (rows || '<div class="tk-empty">No staff found for this store yet.</div>') + '</div>' +
    '<div style="padding:12px;text-align:center;font-size:13px;font-weight:700;color:var(--comms-sub);cursor:pointer" onclick="Tasks.cancelWhoPick()">‹ Back to tasks</div></div>';
}
// v125.51: the picker is on-demand now — backing out must return to the list.
function cancelWhoPick() {
  _pendingAction = null;
  renderTasksScreen();
}
function pickWho(id, name) {
  saveWho({ id: id, name: name });
  clearPin();
  renderTasksScreen();
  // Resume the action that opened the picker (v125.51: identity on demand).
  if (_pendingAction) { var a = _pendingAction; _pendingAction = null; a(); }
}
// v125.51: the list no longer gates on a pick, so switching must open the
// picker explicitly (renderTasksScreen would just show the list again).
function switchWho() { saveWho(null); clearPin(); renderWhoPicker(); }

// ── The list ──────────────────────────────────────────────────────────────────
async function refreshTasks() {
  var acct = isAccountMode();
  try {
    var q;
    if (acct) {
      var store = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : '';
      if (!store) return;
      q = '/api/tasks?store=' + encodeURIComponent(store);
    } else if (_who) {
      q = '/api/tasks?staff_id=' + encodeURIComponent(_who.id);
    } else {
      // v125.51: read-before-pick — the list is keyed by the signed-in store.
      var st = journalStore();
      if (!st) return;
      q = '/api/tasks?store=' + encodeURIComponent(st);
    }
    var res = await api(q);
    if (!res.ok) return;
    var d = await res.json();
    if (d && d.ok) {
      _tasks = d.tasks || [];
      _missed = d.missed || [];
      _history = d.history || [];   // v129.82 — the store's completed past (Meadows idea)
      _date = d.date || null;
      updateNavBadge();
      if (onTasksScreen()) renderList();
    }
  } catch (e) { /* poll again later */ }
}

function updateNavBadge() {
  var open = 0;
  _tasks.forEach(function (t) { if (!t.completed) open++; });
  if (_surveyCount > 0) open += _surveyCount;   // v125.3: the hub badge counts surveys too
  var nav = document.getElementById('nav-tasks');
  if (!nav) return;
  nav.classList.toggle('nav-attn', open > 0);
  var b = document.getElementById('tasksNavBadge');
  if (open > 0) {
    if (!b) {
      b = document.createElement('span');
      b.id = 'tasksNavBadge';
      b.style.cssText = 'position:absolute;top:2px;inset-inline-end:14px;min-width:16px;height:16px;border-radius:8px;background:#e53935;color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 4px';
      nav.style.position = 'relative';
      nav.appendChild(b);
    }
    b.textContent = open > 99 ? '99+' : String(open);
  } else if (b) b.remove();
}

// v126.65 (Matt: "option (all & me). so we can search easy our task and news") —
// a compact filter bar on the feed: 🔍 search across title + body, and an
// All | Me toggle where "Me" = still to do (not completed). Also driven from the
// Updates hub (PR2) via setFilter(). Re-render keeps the search caret at the end.
function matchesFilter(t) {
  if (_scope === 'me' && t.completed) return false;
  var q = _q.trim().toLowerCase();
  if (!q) return true;
  return ((String(t.title || '') + ' ' + String(t.body || '')).toLowerCase().indexOf(q) !== -1);
}
function filterBarHtml() {
  if (_embedded) return '';       // v126.66: the Updates hub renders the one shared bar
  var seg = function (val, label) {
    var on = _scope === val;
    return '<span onclick="Tasks.setScope(\'' + val + '\')" style="cursor:pointer;padding:5px 13px;border-radius:14px;font-size:12px;font-weight:700;' +
      (on ? 'background:var(--brand,#009E3D);color:#fff' : 'background:var(--surface-3);color:var(--comms-sub)') + '">' + label + '</span>';
  };
  return '<div style="display:flex;gap:8px;align-items:center;margin-bottom:10px;flex-wrap:wrap">' +
    '<input id="tkSearch" value="' + esc(_q) + '" oninput="Tasks.onSearch(this.value)" placeholder="🔍 Search tasks…" ' +
    'style="flex:1;min-width:150px;padding:8px 12px;border:1px solid var(--border-strong);border-radius:10px;font-size:13px;box-sizing:border-box">' +
    '<div style="display:flex;gap:6px">' + seg('all', 'All') + seg('me', 'Me') + '</div></div>';
}
function onSearch(v) {
  _q = String(v == null ? '' : v);
  renderList();
  var i = document.getElementById('tkSearch');
  if (i) { try { i.focus(); i.setSelectionRange(i.value.length, i.value.length); } catch (e) {} }
}
function setScope(s) { _scope = (s === 'me') ? 'me' : 'all'; renderList(); }
function setFilter(f) {
  if (f) { if (f.q != null) _q = String(f.q); if (f.scope != null) _scope = (f.scope === 'me') ? 'me' : 'all'; }
  renderList();
}
// v126.66 — the Updates hub host: hide our own bar, and expose the open count for
// the hub's combined nav badge (incomplete tasks + open surveys — same as the badge).
function setEmbedded(on) { _embedded = !!on; }
function openCount() {
  var n = 0;
  _tasks.forEach(function (t) { if (!t.completed) n++; });
  if (_surveyCount > 0) n += _surveyCount;
  return n;
}

// v129.82 — 📜 the store's completed past (Meadows Town Center: "CAN SEE PAST
// - ONCE CLICKED OFF CANT SEE IT"). Read-only, collapsed by default; the
// history rides the normal list payload so opening it costs nothing extra.
function historyCardHtml() {
  if (!_history.length) return '';
  var head = '<div onclick="Tasks.toggleHistory()" style="display:flex;align-items:center;gap:8px;cursor:pointer;padding:12px 14px">' +
    '<span style="font-size:16px">📜</span><span style="font-weight:800;font-size:13.5px">Past tasks</span>' +
    '<span style="font-size:11.5px;color:var(--comms-mute)">' + _history.length + ' completed</span>' +
    '<span style="margin-inline-start:auto;font-size:12px;color:var(--comms-mute)">' + (_showHistory ? '▲ hide' : '▼ show') + '</span></div>';
  var rows = '';
  if (_showHistory) {
    rows = _history.map(function (h, i) {
      // v129.100 (Meadows 9 Aug: "only the subject is showing... cannot
      // open") — each past row is a tap-to-expand accordion carrying the full
      // task brief. Keyed by index (stable within one payload render).
      var open = !!_histOpen[i];
      var detail = '';
      if (open) {
        var when = h.completed_at ? String(h.completed_at).slice(0, 16).replace('T', ' ') : '';
        detail = '<div style="padding:2px 0 6px 20px">' +
          (h.body
            ? '<div style="font-size:12.5px;color:var(--comms-body);white-space:pre-wrap" translate="no">' +
              ((typeof RichText !== 'undefined' && RichText.formatRich) ? RichText.formatRich(h.body, { breaks: false }) : esc(h.body)) + '</div>'
            : '<div style="font-size:12px;color:var(--comms-mute)">No further details on this task.</div>') +
          (when ? '<div style="font-size:11.5px;color:var(--comms-mute);margin-top:4px">Completed ' + esc(when) + '</div>' : '') +
          '</div>';
      }
      return '<div style="padding:7px 14px;border-top:1px solid var(--surface-3);font-size:12.5px">' +
        '<div onclick="Tasks.toggleHistoryItem(' + i + ')" style="cursor:pointer;display:flex;align-items:baseline;gap:4px">' +
        '<span style="color:#059669;font-weight:800">✓</span> <span style="flex:1" translate="no">' + esc(h.title) + '</span>' +
        '<span style="font-size:11px;color:var(--comms-mute)">' + (open ? '▲' : '▼') + '</span></div>' +
        '<div style="font-size:11.5px;color:var(--comms-mute);margin-top:1px">' + esc(h.occurrence_date || '') +
        (h.staff_name ? ' · by <span translate="no">' + esc(h.staff_name) + '</span>' : '') + '</div>' + detail + '</div>';
    }).join('');
  }
  return '<div style="background:var(--card);border-radius:14px;margin-bottom:10px;overflow:hidden;border:1px solid var(--border)">' + head + rows + '</div>';
}
function toggleHistory() { _showHistory = !_showHistory; renderList(); }
function toggleHistoryItem(i) { _histOpen[i] = !_histOpen[i]; renderList(); }

function renderList() {
  var el = root(); if (!el) return;
  var acct = isAccountMode();
  var searching = _q.trim() !== '';
  var cards = _tasks.filter(matchesFilter).map(function (t) {
    var chips = (t.priority === 'urgent' ? '<span class="tk-chip u">URGENT</span>' : '') +
      (t.priority === 'high' ? '<span class="tk-chip h">HIGH</span>' : '') +
      (t.overdue ? '<span class="tk-chip u">⚠ OVERDUE</span>' : '');
    var evid = '';
    if (!acct && !t.completed && t.evidence === 'photo') {
      // v127.36 (Khawaneej): documents count as evidence too — accept the
      // whitelisted file types alongside images.
      evid = '<div class="tk-meta">📷 Photo or 📎 file evidence required</div>' +
        '<input type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" id="tkEv' + t.id + '" onchange="Tasks.evidencePicked(' + t.id + ', this)" style="margin-top:6px;font-size:12px">' +
        '<div id="tkEvList' + t.id + '" style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px"></div>' +
        '<span id="tkEvState' + t.id + '" style="font-size:11.5px;color:var(--comms-mute)"></span>';
    }
    var action = acct ? ''
      : (t.completed
        ? '<div class="tk-donetxt">✓ Done</div>'
        : '<button class="tk-go" onclick="Tasks.completeTask(' + t.id + ')">✓ Mark complete</button>');
    // v129.113 — a task can carry SEVERAL reference files now; one link each.
    var attach = attachLinksHtml(t, 'Tasks.openTaskAttachment', { className: 'tk-meta', prefix: 'View ' });
    // v126.11 (Matt's spec): a task that IS a meeting ("Area meeting 3pm")
    // gets a 📹 Join button — same rooms as the Calls tab, guarded typeof.
    // v127.38: the Join button opens the TASK's own room (audience = the
    // task's audience), not the blanket area room — Calls.joinTask gates via
    // the Worker. joinArea stays the fallback for an older calls bundle.
    var joinCall = (!t.completed && /\b(meeting|call|huddle|briefing|catch[- ]?up)\b/i.test(String(t.title || '')))
      ? '<button class="tk-go" style="background:var(--brand,#009E3D)" onclick="if(typeof Calls!==\'undefined\')(Calls.joinTask?Calls.joinTask(' + t.id + '):Calls.joinArea())">📹 Join the call</button>'
      : '';
    return '<div class="tk-card' + (t.completed ? ' tk-done' : '') + (t.priority === 'urgent' ? ' tk-urgent' : t.priority === 'high' ? ' tk-high' : '') + '">' +
      // v135.1: the task's title, brief and author are what Head Office wrote: never translated.
      '<div class="tk-title">' + chips + '<span translate="no">' + esc(t.title) + '</span></div>' +
      (t.body ? '<div class="tk-body" translate="no">' + ((typeof RichText !== 'undefined' && RichText.formatRich) ? RichText.formatRich(t.body, { breaks: false }) : esc(t.body)) + '</div>' : '') +
      '<div class="tk-meta">' + (t.created_by ? '<span translate="no">' + esc(t.created_by) + '</span>' : 'Head Office') +
      (t.recurrence !== 'none' ? ' · repeats ' + esc(t.recurrence) : (t.deadline ? ' · Due by ' + esc(t.deadline) : '')) + '</div>' +
      attach + evid + joinCall + action + '</div>';
  }).join('');
  el.innerHTML = '<div class="tk-shell">' +
    '<div class="tk-head"><div style="font-size:22px">✅</div><div><div class="tk-hname">Tasks</div>' +
    '<div class="tk-hsub">' + (_date ? 'Today · ' + esc(_date) : 'Today') + (acct ? '' : ' · 🔒 Completions are visible to Head Office') + '</div></div>' +
    (!acct ? '<div style="margin-inline-start:auto;font-size:11.5px;background:rgba(255,255,255,.15);border-radius:12px;padding:4px 10px;cursor:pointer" onclick="Tasks.switchWho()">' + (_who ? '<span translate="no">' + esc(_who.name) + '</span> · switch' : 'Choose your name') + '</div>' : '') +
    '</div>' +
    // While searching, drop the journal/attention/surveys chrome so the results
    // are clean; the All | Me toggle keeps them (it's a view of the same day).
    '<div class="tk-feed">' + filterBarHtml() + (searching ? '' : (attentionStripHtml() + journalHtml() + surveysCardHtml())) +
    (cards || '<div class="tk-empty">' +
      // v129.50 — the flagship TRAIN carries the empty state ("all done —
      // next one's coming"). Searching keeps plain text: an illustration on a
      // failed search reads as a broken result, not a calm one.
      (searching ? '' : '<div style="margin-bottom:10px">' + (typeof Artwork !== 'undefined' && Artwork.trainSvg ? Artwork.trainSvg(96) : '') + '</div>') +
      (searching
      ? 'No tasks match "' + esc(_q.trim()) + '".'
      : (_scope === 'me' ? 'Nothing left to do — all caught up. 🎉' : 'Nothing due today — new tasks from Head Office show here. 🎉')) + '</div>') +
    (searching ? '' : historyCardHtml()) +
    '</div></div>';
}

// v154 — view the reference file HO attached to a task (photo or document).
// Fetched on demand with the same read identity as the task list (staff_id /
// store / Bearer). Images open in an in-page viewer; documents download
// (browsers block top-level navigation to data: URLs).
function taskReadId() {
  if (isAccountMode()) return '';
  if (_who) return '&staff_id=' + encodeURIComponent(_who.id);
  var st = journalStore();
  return st ? '&store=' + encodeURIComponent(st) : '';
}
// v129.113 — `idx` picks which of the task's files to open.
async function openTaskAttachment(taskId, idx) {
  try {
    var res = await api('/api/tasks/attachment?task_id=' + encodeURIComponent(taskId) + taskReadId());
    if (!res.ok) { if (typeof showToast !== 'undefined') showToast('Could not open the attachment', 'error'); return; }
    var d = await res.json();
    if ((!d || !d.ok || !openFromPayload(d, Number(idx) || 0)) && typeof showToast !== 'undefined') {
      showToast('Could not open the attachment', 'error');
    }
  } catch (e) { if (typeof showToast !== 'undefined') showToast('Could not open the attachment', 'error'); }
}
// v129.112 — the v125.81 blob-opener logic moved to src/shared/open-file.js so the
// ADMIN app stops re-deriving it (Head Office's PDF). Behaviour here is unchanged.
// v129.113 — openTaskAttachment now goes via openFromPayload (which calls the same
// shared opener) so it can pick a file by index. Kept and window-bound because
// other code paths and a cached bundle's onclick may still reach it.
function openTaskDataUrl(dataUrl, filename, contentType) {
  return openStoredFile(dataUrl, filename, contentType);
}

// ── Evidence (canvas compression — chat A6-1 pattern) ────────────────────────
// v129.109 — SEVERAL files. Each one goes through exactly the same reader and
// compressor as before; what changed is that the results accumulate into an
// array instead of replacing a single slot. Files are processed one at a time
// (a phone picking 3 photos at once would otherwise decode them in parallel and
// spike memory on the cheap Android tills).

// One chip per attached file, with a way to take it back off. Rendering from
// the array means the strip can never disagree with what will be sent.
function renderEvidenceChips(taskId) {
  var box = document.getElementById('tkEvList' + taskId);
  if (!box) return;
  var list = evidenceList(taskId);
  var names = evidenceNames(taskId);
  if (!list.length) { box.innerHTML = ''; return; }
  box.innerHTML = list.map(function (d, i) {
    var isImg = String(d || '').indexOf('data:image/') === 0;
    var label = names[i] || (isImg ? 'Photo ' + (i + 1) : 'File ' + (i + 1));
    var thumb = isImg
      ? '<img src="' + d + '" alt="" style="width:34px;height:34px;object-fit:cover;border-radius:6px;display:block">'
      : '<div style="width:34px;height:34px;border-radius:6px;background:var(--surface-3);display:flex;align-items:center;justify-content:center;font-size:16px">📎</div>';
    return '<div style="display:flex;align-items:center;gap:6px;border:1px solid var(--border,#dde3e8);border-radius:8px;padding:3px 6px;background:var(--card)">' +
      thumb +
      '<span style="font-size:11px;max-width:96px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"' + (names[i] ? ' translate="no"' : '') + '>' + esc(label) + '</span>' +
      '<span onclick="Tasks.evidenceRemove(' + taskId + ',' + i + ')" title="Remove" ' +
        'style="cursor:pointer;font-weight:800;color:var(--comms-err);font-size:13px;line-height:1">×</span>' +
      '</div>';
  }).join('');
}

function evidenceRemove(taskId, idx) {
  var list = evidenceList(taskId).slice();
  var names = evidenceNames(taskId).slice();
  list.splice(idx, 1);
  names.splice(idx, 1);
  _evidence[taskId] = list;
  _evidenceName[taskId] = names;
  if (!list.length) { delete _evidence[taskId]; delete _evidenceName[taskId]; }
  renderEvidenceChips(taskId);
  var state = document.getElementById('tkEvState' + taskId);
  if (state) state.textContent = list.length ? ' ✓ ' + list.length + ' attached' : '';
}

// Read + compress ONE file. Resolves with the data URL, or null with the reason
// already shown — a file that cannot be used must never silently look attached.
function readEvidenceFile(f, state) {
  return new Promise(function (resolve) {
    // v127.36 — non-image files skip the canvas compressor: read straight to a
    // data URL, bounded (~1.1MB raw → ≤1.5M chars, the Worker's cap). The
    // Worker's validateEvidence whitelist is the real gate; this is UX.
    if (f.type && f.type.indexOf('image/') !== 0) {
      if (f.size > 1100000) { if (state) state.textContent = ' ' + (f.name || 'That file') + ' is too large — keep files under 1MB.'; return resolve(null); }
      var rd = new FileReader();
      rd.onload = function () {
        var out = String(rd.result || '');
        if (out.length > 1500000) { if (state) state.textContent = ' ' + (f.name || 'That file') + ' is too large — keep files under 1MB.'; return resolve(null); }
        resolve(out);
      };
      rd.onerror = function () { if (state) state.textContent = ' Could not read ' + (f.name || 'that file') + '.'; resolve(null); };
      rd.readAsDataURL(f);
      return;
    }
    var img = new Image();
    var url = URL.createObjectURL(f);
    img.onload = function () {
      try {
        var scale = Math.min(1, 1280 / Math.max(img.width, img.height));
        var c = document.createElement('canvas');
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        var out = null;
        var qualities = [0.72, 0.55, 0.4];
        for (var i = 0; i < qualities.length; i++) {
          out = c.toDataURL('image/jpeg', qualities[i]);
          if (out.length <= 400000) break;
        }
        if (out && out.length <= 400000) resolve(out);
        else { if (state) state.textContent = ' One photo was too large even compressed — try another.'; resolve(null); }
      } catch (e) {
        if (state) state.textContent = ' Could not read that image.';
        resolve(null);
      } finally { URL.revokeObjectURL(url); }
    };
    img.onerror = function () { URL.revokeObjectURL(url); if (state) state.textContent = ' Could not read that image.'; resolve(null); };
    img.src = url;
  });
}

async function evidencePicked(taskId, input) {
  var files = (input && input.files) ? Array.prototype.slice.call(input.files) : [];
  var state = document.getElementById('tkEvState' + taskId);
  if (!files.length) return;
  var have = evidenceList(taskId);
  var room = TK_MAX_EVIDENCE - have.length;
  if (room <= 0) {
    if (state) state.textContent = ' Up to ' + TK_MAX_EVIDENCE + ' files — remove one first.';
    if (input) input.value = '';
    return;
  }
  var over = files.length > room;
  files = files.slice(0, room);
  if (state) state.textContent = ' Reading…';
  var added = evidenceList(taskId).slice();
  var names = evidenceNames(taskId).slice();
  for (var i = 0; i < files.length; i++) {
    var out = await readEvidenceFile(files[i], state);   // one at a time — see the header
    if (!out) continue;
    added.push(out);
    names.push(files[i].name || null);
  }
  _evidence[taskId] = added;
  _evidenceName[taskId] = names;
  if (!added.length) { delete _evidence[taskId]; delete _evidenceName[taskId]; }
  // The input is cleared so picking the SAME file again still fires onchange —
  // otherwise re-adding a photo you just removed silently does nothing.
  if (input) input.value = '';
  renderEvidenceChips(taskId);
  if (added.length && state) {
    state.textContent = ' ✓ ' + added.length + ' attached'
      + (over ? ' (up to ' + TK_MAX_EVIDENCE + ' — the rest were not added)' : '');
  }
}

// ── Complete (per store per occurrence; PIN-attributed person) ────────────────
function completeTask(id) {
  if (isAccountMode()) return;
  var t = _tasks.find(function (x) { return x.id === id; });
  if (t && t.evidence === 'photo' && !evidenceList(id).length) {
    if (typeof showToast !== 'undefined') showToast('This task needs evidence first — attach a photo or file below the task.', 'error');
    return;
  }
  // v125.51: identity on demand — reading never asked who you are, so ask now.
  if (!_who) { _pendingAction = function () { completeTask(id); }; renderWhoPicker(); return; }
  if (pinFresh()) { doComplete(id); return; }
  askPin(function () { doComplete(id); });
}

async function doComplete(id) {
  if (_completing) return;
  _completing = true;
  try {
    var res = await api('/api/tasks/complete', {
      method: 'POST',
      // v129.109 — `photos`/`photo_names` are the arrays; `photo`/`photo_name`
      // stay populated with the FIRST file so a Worker still on the old build
      // (the two deploy separately) records evidence rather than nothing.
      body: JSON.stringify({
        task_id: id, occurrence_date: _date, auth_staff_id: _who.id, auth_pin: _pin,
        photos: evidenceList(id), photo_names: evidenceNames(id),
        photo: evidenceList(id)[0] || null, photo_name: evidenceNames(id)[0] || null,
      }),
    });
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) {
      _pinAt = Date.now();
      delete _evidence[id];
      delete _evidenceName[id];
      var t = _tasks.find(function (x) { return x.id === id; });
      if (t) t.completed = true;
      updateNavBadge();
      renderList();
    } else {
      clearPin();
      if (res.status === 401 || res.status === 429) askPin(function () { doComplete(id); }, (d && d.error) || 'PIN check failed — try again');
      else if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not complete', 'error');
    }
  } catch (e) {
    if (typeof showToast !== 'undefined') showToast('No connection — try again', 'error');
  } finally {
    _completing = false;
  }
}

// The PIN prompt — memory only, 5-min idle expiry (chat A3 posture).
function askPin(then, errMsg) {
  var el = root(); if (!el) return;
  var old = document.getElementById('tkPinModal');
  if (old) old.remove();
  var wrap = document.createElement('div');
  wrap.id = 'tkPinModal';
  wrap.className = 'tk-modal';
  wrap.innerHTML = '<div class="tk-mcard">' +
    '<div style="font-weight:700;font-size:15px;color:var(--text)"><span translate="no">' + esc(_who ? _who.name : '') + '</span> — confirm it\'s you</div>' +
    '<div style="font-size:12.5px;color:var(--comms-sub);margin-top:6px">' + (errMsg ? esc(errMsg) : 'Enter your till PIN to complete this task in your name.') + '</div>' +
    '<input id="tkPinInput" class="tk-mpin" type="password" inputmode="numeric" autocomplete="off" maxlength="8">' +
    '<button class="tk-mbtn" id="tkPinGo">Confirm</button>' +
    '<button class="tk-mcancel" id="tkPinCancel">Cancel</button></div>';
  el.appendChild(wrap);
  var input = document.getElementById('tkPinInput');
  input.focus();
  function go() {
    var v = String(input.value || '').trim();
    if (!v) return;
    _pin = v; _pinAt = Date.now();
    wrap.remove();
    then();
  }
  document.getElementById('tkPinGo').onclick = go;
  input.onkeydown = function (e) { if (e.key === 'Enter') go(); };
  document.getElementById('tkPinCancel').onclick = function () { wrap.remove(); };
}

// ── Background badge (light: every 5 min, visible page + identity only) ──────
// v125.16 (review C): also refresh the journal so _surveyCount stays live — the
// Tasks tab badge counts surveys, so without this a new survey stayed invisible
// (badge/amber unset) until the user opened Tasks at least once this session.
if (typeof window !== 'undefined') {
  setInterval(function () {
    if (document.hidden) return;
    if (isAccountMode() || loadWho()) { refreshTasks(); refreshJournal(); }
  }, 5 * 60 * 1000);
}

// ── Window bindings (classic-script bundle contract) ─────────────────────────
if (typeof window !== 'undefined') {
  window.renderTasksScreen = renderTasksScreen;
}

// v125.14 CRITICAL: handlers are EXPORTED, not on `window.Tasks = {…}` — esbuild's
// `globalName:'Tasks'` assigns the module EXPORTS to window.Tasks, clobbering any
// in-body assignment, so an unexported handler = a dead `onclick="Tasks.x()"`.
// (Root-cause of the review CRITICAL; see the news bundle for the full note.)
export {
  esc, initials,
  addSuggested, editBrief, saveBrief, addTodo, toggleTodo, deleteTodo,
  pickWho, switchWho, cancelWhoPick, completeTask, evidencePicked,
  evidenceRemove,                                     // v129.109 — remove one attached file
  openTaskAttachment,
  onSearch, setScope, setFilter,           // v126.65 — search + All/Me filter (also driven by the Updates hub)
  setEmbedded, openCount,                  // v126.66 — Updates hub host + combined-badge count
  toggleHistory,
  toggleHistoryItem,                       // v129.100 — expand a past task's details (Meadows bug)                           // v129.82 — 📜 past tasks (Meadows idea)
};
